/**
 * AnalyticsScreen — tableau de statistiques de productivité.
 * Score du jour, bars tâches/quadrant, ligne focus hebdo, donut sources,
 * top 5 projets, export PDF (stub).
 */
import type { JSX } from 'react';
import { Download } from 'lucide-react';
import { UniqueButton } from '@components/UniqueButton';
import { UniqueCard } from '@components/UniqueCard';
import { UpgradeBanner } from '@components/subscription/UpgradeGate';
import { useTaskStore } from '@store/taskStore';
import { usePlan } from '@/hooks/usePlan';
import type { Quadrant, TaskSource } from '@store/types';

const GOLD = 'var(--color-primary)';
const GOLD_DARK = 'var(--color-primary-dark)';

const QUADRANT_COLORS: Record<Quadrant, string> = {
  Q1: 'var(--color-urgent)',
  Q2: 'var(--color-important)',
  Q3: 'var(--color-normal)',
  Q4: 'var(--color-defer)',
};

export default function AnalyticsScreen(): JSX.Element {
  const { limits } = usePlan();
  const tasks = useTaskStore((s) => s.tasks);
  const done = tasks.filter((t) => t.status === 'done');
  const total = tasks.length || 1;
  const score = Math.round((done.length / total) * 100);

  const byQuadrant: Record<Quadrant, number> = { Q1: 0, Q2: 0, Q3: 0, Q4: 0 };
  tasks.forEach((t) => {
    byQuadrant[t.quadrant]++;
  });

  const sources: Record<TaskSource, number> = {
    email: 0,
    call: 0,
    manual: 0,
    ai: 0,
    inherited: 0,
  };
  tasks.forEach((t) => {
    sources[t.source]++;
  });

  const focusWeek = [42, 65, 58, 73, 88, 60, 35];
  const focusDays = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1>Analytics</h1>
          <p className="mt-2 text-sm text-[color:var(--color-text-medium)]">
            Vos indicateurs clés de productivité.
          </p>
        </div>
        <UniqueButton
          leftIcon={<Download size={16} />}
          disabled={!limits.pdfExport}
          title={!limits.pdfExport ? 'Export PDF disponible à partir du plan Essentiel' : undefined}
        >
          Exporter PDF
        </UniqueButton>
      </div>

      {!limits.analytics30Days && (
        <UpgradeBanner
          message="Analytics limités aux 7 derniers jours."
          requiredPlan="essential"
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
        <UniqueCard variant="glow" padding="lg" className="md:col-span-1">
          <p className="text-xs uppercase font-bold text-[color:var(--color-primary-dark)]">
            Score de productivité
          </p>
          <p className="text-6xl font-extrabold my-2" style={{ color: GOLD_DARK }}>
            {score}
          </p>
          <p className="text-xs text-[color:var(--color-text-medium)]">
            {done.length} tâche(s) terminée(s) sur {tasks.length}
          </p>
        </UniqueCard>

        <UniqueCard padding="lg" className="md:col-span-2">
          <h3 className="text-sm font-bold mb-4">Tâches par quadrant</h3>
          <BarsChart data={byQuadrant} colors={QUADRANT_COLORS} />
        </UniqueCard>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        <UniqueCard padding="lg">
          <h3 className="text-sm font-bold mb-4">Temps de focus (7 jours)</h3>
          <LineChart values={focusWeek} labels={focusDays} />
        </UniqueCard>

        <UniqueCard padding="lg">
          <h3 className="text-sm font-bold mb-4">Répartition des sources</h3>
          <Donut data={sources} />
        </UniqueCard>
      </div>

      <UniqueCard padding="lg">
        <h3 className="text-sm font-bold mb-4">Top projets actifs</h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-[color:var(--color-text-medium)] uppercase border-b border-[color:var(--color-border)]">
              <th className="py-2">Projet</th>
              <th>Tâches</th>
              <th>Complétion</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Budget 2026', 8, 72],
              ['Stratégie 2026', 5, 40],
              ['Onboarding RH', 4, 25],
              ['Plan logistique', 3, 66],
              ['Comité mensuel', 2, 90],
            ].map(([name, count, pct]) => (
              <tr
                key={name as string}
                className="border-b border-[color:var(--color-border)] last:border-0"
              >
                <td className="py-3 font-semibold">{name}</td>
                <td>{count}</td>
                <td className="w-1/3">
                  <div
                    className="h-2 rounded-full overflow-hidden"
                    style={{ background: 'var(--color-primary-glow)' }}
                  >
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${pct}%`,
                        background: `linear-gradient(90deg, ${GOLD}, ${GOLD_DARK})`,
                      }}
                    />
                  </div>
                  <span className="text-[11px] text-[color:var(--color-text-medium)]">
                    {pct}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </UniqueCard>
    </div>
  );
}

function BarsChart({
  data,
  colors,
}: {
  data: Record<Quadrant, number>;
  colors: Record<Quadrant, string>;
}): JSX.Element {
  const max = Math.max(1, ...Object.values(data));
  return (
    <div className="flex items-end gap-4 h-40">
      {(Object.keys(data) as Quadrant[]).map((q) => {
        const h = (data[q] / max) * 100;
        return (
          <div key={q} className="flex-1 flex flex-col items-center gap-2">
            <div
              className="w-full rounded-t-lg transition-all"
              style={{
                height: `${h}%`,
                minHeight: 6,
                background: colors[q],
              }}
              aria-label={`${q} : ${data[q]} tâches`}
            />
            <span className="text-[11px] font-bold">{q}</span>
            <span className="text-[11px] text-[color:var(--color-text-medium)]">
              {data[q]}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function LineChart({
  values,
  labels,
}: {
  values: number[];
  labels: string[];
}): JSX.Element {
  const max = Math.max(...values);
  const points = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * 100;
      const y = 100 - (v / max) * 100;
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <div>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-32">
        <defs>
          <linearGradient id="lineFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#F5C200" stopOpacity={0.4} />
            <stop offset="100%" stopColor="#F5C200" stopOpacity={0} />
          </linearGradient>
        </defs>
        <polyline
          fill="url(#lineFill)"
          stroke="none"
          points={`0,100 ${points} 100,100`}
        />
        <polyline
          fill="none"
          stroke="#E6A800"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          points={points}
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      <div className="flex justify-between text-[10px] text-[color:var(--color-text-medium)] mt-1">
        {labels.map((l, i) => (
          <span key={i}>{l}</span>
        ))}
      </div>
    </div>
  );
}

function Donut({ data }: { data: Record<TaskSource, number> }): JSX.Element {
  const entries = Object.entries(data).filter(([, v]) => v > 0) as [TaskSource, number][];
  const total = entries.reduce((s, [, v]) => s + v, 0) || 1;
  const colors: Record<TaskSource, string> = {
    email: '#F5C200',
    call: '#42A5F5',
    manual: '#BDBDBD',
    ai: '#E6A800',
    inherited: '#66BB6A',
  };
  const labels: Record<TaskSource, string> = {
    email: 'Email',
    call: 'Appel',
    manual: 'Manuel',
    ai: 'IA',
    inherited: 'Hérité',
  };

  let offset = 0;
  const radius = 40;
  const circ = 2 * Math.PI * radius;

  return (
    <div className="flex items-center gap-5">
      <svg viewBox="0 0 100 100" className="w-32 h-32">
        <circle cx={50} cy={50} r={radius} fill="none" stroke="var(--color-primary-glow)" strokeWidth={14} />
        {entries.map(([source, value]) => {
          const pct = value / total;
          const dash = pct * circ;
          const el = (
            <circle
              key={source}
              cx={50}
              cy={50}
              r={radius}
              fill="none"
              stroke={colors[source]}
              strokeWidth={14}
              strokeDasharray={`${dash} ${circ - dash}`}
              strokeDashoffset={-offset}
              transform="rotate(-90 50 50)"
            />
          );
          offset += dash;
          return el;
        })}
      </svg>
      <ul className="flex flex-col gap-1.5 text-xs">
        {entries.map(([source, value]) => (
          <li key={source} className="flex items-center gap-2">
            <span
              className="w-2.5 h-2.5 rounded-full"
              style={{ background: colors[source] }}
              aria-hidden
            />
            <span className="font-semibold">{labels[source]}</span>
            <span className="text-[color:var(--color-text-medium)]">
              ({value})
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
